import { Button, Grid } from "@mui/material";
import { PlusLg } from "react-bootstrap-icons";
import { useDispatch, useSelector } from "react-redux";
import { useItems } from "../../features/Fetch/Items";
import { ModalProps, setProps } from "../../features/Slices/modalSlice";
import { BucketNodeItem, BucketNodeRelationItem, BucketNodeRelationType, BucketNodeSnapshotResponse, BudgetItem, CurrentUser, FamilyBucketLabel, FamilyBucketPluralLabel, TransactionItem } from "../../types";
import EditBucketNode, { EditBucketNodeModel } from "../Modals/EditBucketNode";
import BucketNode from "../ListItems/BucketNode";

const FamilyBuckets = () => {
	const repo = useItems();
	const dispatch = useDispatch();

	const modal: ModalProps = useSelector((state: any) => state.modal.value);
	const budgets: BudgetItem[] = useSelector((state: any) => state.budgets.value) ?? [];
	const snapshot: BucketNodeSnapshotResponse | undefined = useSelector((state: any) => state.bucketNodes.value);
	const checkRequests: TransactionItem[] = useSelector((state: any) => state.checkRequests.value) ?? [];
	const transactions: TransactionItem[] = useSelector((state: any) => state.transactions.value) ?? [];
	const user: CurrentUser = useSelector((state: any) => state.userData.value);

	const definitions = (user.bucketDefinitions?.definitions ?? [])
		.filter(d => d.isEnabled)
		.sort((a, b) => a.sortOrder - b.sortOrder);
	const rootTypeKeys = definitions
		.filter(d => d.isIndependent && !d.parentBucketTypeKey && d.bucketTypeKey.toLowerCase() !== "budget")
		.map(d => d.bucketTypeKey);
	const nodeById = (snapshot?.nodes ?? []).reduce((result: { [key: number]: BucketNodeItem }, node) => {
		result[node.bucketNodeId] = node;
		return result;
	}, {} as { [key: number]: BucketNodeItem });
	const childrenByParent = (snapshot?.relations ?? [])
		.filter((r: BucketNodeRelationItem) => r.relationType === BucketNodeRelationType.Hierarchy)
		.reduce((result: { [key: number]: number[] }, relation) => {
			if (!result[relation.sourceBucketNodeId]) result[relation.sourceBucketNodeId] = [];
			result[relation.sourceBucketNodeId].push(relation.targetBucketNodeId);
			return result;
		}, {} as { [key: number]: number[] });
	const parentByChild = (snapshot?.relations ?? [])
		.filter((r: BucketNodeRelationItem) => r.relationType === BucketNodeRelationType.Hierarchy)
		.reduce((result: { [key: number]: number }, relation) => {
			result[relation.targetBucketNodeId] = relation.sourceBucketNodeId;
			return result;
		}, {} as { [key: number]: number });

	const budgetUsageById: { [key: number]: number } = {};
	budgets.forEach(b => {
		const seen = new Set<number>();
		(b.bucketNodes ?? []).forEach(bn => {
			if (bn.bucketNodeId && !seen.has(bn.bucketNodeId)) {
				seen.add(bn.bucketNodeId);
				budgetUsageById[bn.bucketNodeId] = (budgetUsageById[bn.bucketNodeId] ?? 0) + 1;
			}
		});
	});
	const transactionUsageById: { [key: number]: number } = {};
	const tallyTransaction = (item: TransactionItem) => {
		const seen = new Set<number>();
		(item.allocations ?? []).forEach(allocation => {
			(allocation.bucketNodes ?? []).forEach(bn => {
				if (bn.bucketNodeId && !seen.has(bn.bucketNodeId)) {
					seen.add(bn.bucketNodeId);
					transactionUsageById[bn.bucketNodeId] = (transactionUsageById[bn.bucketNodeId] ?? 0) + 1;
				}
			});
		});
	};
	transactions.forEach(tallyTransaction);
	checkRequests.forEach(tallyTransaction);

	function GetUsage(bucketNodeId: number) {
		return {
			budgets: budgetUsageById[bucketNodeId] ?? 0,
			transactions: transactionUsageById[bucketNodeId] ?? 0
		};
	}

	function FormatUsageMessage(bucketNodeId: number) {
		const { budgets: bCount, transactions: tCount } = GetUsage(bucketNodeId);
		const parts: string[] = [];
		if (bCount > 0) parts.push(`${bCount} budget${bCount === 1 ? "" : "s"}`);
		if (tCount > 0) parts.push(`${tCount} transaction${tCount === 1 ? "" : "s"}`);
		if (parts.length === 0) return "";
		return `In use by ${parts.join(" and ")}`;
	}

	return (<>
		{rootTypeKeys.map(rootTypeKey => {
			const rootNodes = (snapshot?.nodes ?? [])
				.filter(n => n.bucketTypeKey.toLowerCase() === rootTypeKey.toLowerCase())
				.filter(n => !parentByChild[n.bucketNodeId])
				.sort((a, b) => a.displayName.localeCompare(b.displayName));

			return (
				<div className="col-12" key={rootTypeKey}>
					<h3 className="text-app mt-4 mb-2">{FamilyBucketPluralLabel(user, rootTypeKey, rootTypeKey)}</h3>
					{rootNodes.length === 0 && <div className="text-app mb-2">No nodes yet.</div>}
					{rootNodes.map(node => (
						<BucketNode
							key={node.bucketNodeId}
							Node={node}
							Depth={0}
							ChildrenByParent={childrenByParent}
							NodeById={nodeById}
							UsageLabelFor={FormatUsageMessage}
							OnEdit={OpenEditModal}
							OnAddChild={OpenNewChildModal} />
					))}
					<div className="btn btn-category btn-list text-app" onClick={() => OpenNewRootModal(rootTypeKey)}>
						<h3>
							<PlusLg /> Add New {FamilyBucketLabel(user, rootTypeKey, rootTypeKey)}
						</h3>
					</div>
				</div>
			);
		})}
	</>);

	function HideModal() {
		dispatch(setProps({ ...modal, IsOpen: false }));
	}

	function SaveNode(item: EditBucketNodeModel) {
		if (item.bucketNodeId && item.bucketNodeId > 0) {
			repo.UpdateCustomBucketNodeAsync({
				bucketNodeId: item.bucketNodeId,
				bucketTypeKey: item.bucketTypeKey,
				displayName: item.displayName,
				isInactive: item.isInactive,
				isViewTransactions: item.isViewTransactions,
				parentBucketNodeId: item.parentBucketNodeId
			});
		} else {
			repo.AddCustomBucketNodeAsync({
				bucketTypeKey: item.bucketTypeKey,
				displayName: item.displayName,
				isInactive: item.isInactive,
				isViewTransactions: item.isViewTransactions,
				parentBucketNodeId: item.parentBucketNodeId
			});
		}
		HideModal();
	}

	function DeleteNode(item: EditBucketNodeModel) {
		if (!item.bucketNodeId) return;
		repo.DeleteCustomBucketNodeAsync(item.bucketNodeId);
		HideModal();
	}

	function OpenNewRootModal(rootTypeKey: string) {
		const item: EditBucketNodeModel = {
			bucketTypeKey: rootTypeKey,
			displayName: "",
			isInactive: false,
			isViewTransactions: true
		};
		OpenModal(item, undefined);
	}

	function OpenNewChildModal(parentNodeId: number, childTypeKey: string) {
		const item: EditBucketNodeModel = {
			bucketTypeKey: childTypeKey,
			parentBucketNodeId: parentNodeId,
			displayName: "",
			isInactive: false,
			isViewTransactions: true
		};
		OpenModal(item, undefined);
	}

	function OpenEditModal(node: BucketNodeItem) {
		const item: EditBucketNodeModel = {
			bucketNodeId: node.bucketNodeId,
			bucketTypeKey: node.bucketTypeKey,
			parentBucketNodeId: parentByChild[node.bucketNodeId],
			displayName: node.displayName,
			isInactive: node.isInactive ?? false,
			isViewTransactions: node.isViewTransactions ?? true
		};

		const hasChildren = (childrenByParent[node.bucketNodeId]?.length ?? 0) > 0;
		const usage = GetUsage(node.bucketNodeId);
		const inUse = usage.budgets > 0 || usage.transactions > 0;
		const canDelete = !hasChildren && !inUse;
		const usageMessage = hasChildren
			? "Cannot delete: this has child items"
			: FormatUsageMessage(node.bucketNodeId);
		OpenModal(item, canDelete ? () => DeleteNode(item) : undefined, usageMessage);
	}

	function OpenModal(item: EditBucketNodeModel, onDelete?: () => void, usageMessage?: string) {
		const nodeLabel = FamilyBucketLabel(user, item.bucketTypeKey, item.bucketTypeKey);
		const newModal = {
			...modal,
			Body: <EditBucketNode Item={item} NodeLabel={nodeLabel} UsageMessage={usageMessage} OnSave={SaveNode} OnDelete={onDelete} Close={HideModal} />,
			IsOpen: true,
			WasOpen: false
		};
		dispatch(setProps(newModal));
	}
};

export default FamilyBuckets;
